import { useCallback, useEffect, useRef, useState } from 'react';

import { findFeedbackTarget, FeedbackTarget } from './feedback-target';

export interface HighlightBox {
  top: number;
  left: number;
  width: number;
  height: number;
  label: string;
}

interface Options {
  enabled: boolean;
  onPick: (target: FeedbackTarget) => void;
  onExit: () => void;
}

// Swallowed while picking so a click on the page comments on it instead of operating it.
const BLOCKED_EVENTS = [
  'pointerdown',
  'pointerup',
  'mousedown',
  'mouseup',
  'dblclick',
  'contextmenu',
] as const;

function toBox(target: FeedbackTarget): HighlightBox {
  const rect = target.element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    label: target.feedbackId,
  };
}

export function useRegionPicker({ enabled, onPick, onExit }: Options) {
  const [highlight, setHighlight] = useState<HighlightBox | null>(null);
  const targetRef = useRef<FeedbackTarget | null>(null);
  const frameRef = useRef<number | null>(null);
  const callbacks = useRef({ onPick, onExit });
  callbacks.current = { onPick, onExit };

  const clearHighlight = useCallback(() => setHighlight(null), []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const show = (target: FeedbackTarget | null) => {
      targetRef.current = target;
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        setHighlight(target ? toBox(target) : null);
      });
    };

    const onMove = (event: MouseEvent) => {
      const target = findFeedbackTarget(event.target as Element | null);
      if (target?.element !== targetRef.current?.element) {
        show(target);
      }
    };
    const onScroll = () => show(targetRef.current);
    const onBlocked = (event: Event) => {
      if (findFeedbackTarget(event.target as Element | null)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const onClick = (event: MouseEvent) => {
      const target = findFeedbackTarget(event.target as Element | null);
      if (!target) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      callbacks.current.onPick(target);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        callbacks.current.onExit();
      }
    };

    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('scroll', onScroll, true);
    document.addEventListener('click', onClick, true);
    BLOCKED_EVENTS.forEach((name) =>
      document.addEventListener(name, onBlocked, true)
    );
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('click', onClick, true);
      BLOCKED_EVENTS.forEach((name) =>
        document.removeEventListener(name, onBlocked, true)
      );
      window.removeEventListener('keydown', onKey);
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      targetRef.current = null;
    };
  }, [enabled]);

  return { highlight, clearHighlight };
}
